import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import i18n from "@/i18n";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MementoLogo, SalonUpLogo } from "@/components/Brand";

const SALON_IMG = "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoYWlyJTIwc2Fsb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODU1MDM5OTB8MA&ixlib=rb-4.1.0&q=85";

function GoogleG({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.75-6-6.15S8.7 5.9 12 5.9c1.9 0 3.15.8 3.88 1.5l2.65-2.55C16.83 3.25 14.6 2.25 12 2.25 6.87 2.25 2.7 6.42 2.7 11.55S6.87 20.85 12 20.85c6.94 0 9.3-4.87 9.3-7.4 0-.5-.05-.9-.13-1.25H12z"/>
    </svg>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { login, loginWithGoogle, verifyLoginOtp } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r?.otp_required) {
        setOtpStep(true);
        setOtpMessage(r.message || t("auth.otpSent"));
        toast.success(t("auth.twoStepRequired"));
      } else {
        toast.success(i18n.getFixedT(r.language || i18n.language)("auth.welcomeBack", { name: r.name }));
        nav(r.role === "agency_admin" ? "/admin" : r.role === "agency_receptionist" ? "/admin/agency/pipeline" : "/app");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(t("auth.tooManyAttempts"));
      } else if (status === 423) {
        toast.error(t("auth.accountLocked"));
      } else {
        toast.error(err?.response?.data?.detail || t("auth.loginError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyLoginOtp(email, otpCode);
      toast.success(i18n.getFixedT(u.language || i18n.language)("auth.welcomeBack", { name: u.name }));
      nav(u.role === "agency_admin" ? "/admin" : u.role === "agency_receptionist" ? "/admin/agency/pipeline" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.invalidCode"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--sp-bg)]">
      <div className="relative flex items-center justify-center p-8 lg:p-16">
        <div className="absolute inset-0 memento-soft-glow pointer-events-none" />
        <div className="w-full max-w-md sp-anim relative">
          <MementoLogo withTagline size="lg" />

          <span className="label-eyebrow block mt-10">{t("auth.loginEyebrow")}</span>
          <h1 className="font-display text-4xl font-black mt-3 mb-2 tracking-tight"><Trans i18nKey="auth.loginTitle" components={[<span className="text-gradient" key="0" />]} /></h1>
          <p className="text-[color:var(--sp-text-secondary)] mb-8">{t("auth.loginSubtitle")}</p>

          <button data-testid="login-google-btn" type="button" onClick={loginWithGoogle} className="sp-btn-google">
            <GoogleG /> {t("auth.continueWithGoogle")}
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[color:var(--sp-border)]" />
            <span className="text-xs text-[color:var(--sp-text-muted)] uppercase tracking-widest">{t("common.or")}</span>
            <div className="flex-1 h-px bg-[color:var(--sp-border)]" />
          </div>

          <form onSubmit={otpStep ? submitOtp : submit} className="space-y-4">
            {!otpStep ? (
              <>
                <div>
                  <label className="text-sm font-bold">{t("auth.email")}</label>
                  <input data-testid="login-email-input" type="email" required className="sp-input mt-1.5" placeholder={t("auth.emailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-bold">{t("auth.password")}</label>
                  <input data-testid="login-password-input" type="password" required className="sp-input mt-1.5" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <button data-testid="login-submit-button" disabled={loading} className="sp-btn-primary w-full mt-2">
                  {loading ? t("auth.signingIn") : t("auth.signIn")}
                </button>
              </>
            ) : (
              <>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900" data-testid="login-otp-message">
                  {otpMessage} <Trans i18nKey="auth.otpHint" values={{ email }} components={[<b key="0" />]} />
                </div>
                <div>
                  <label className="text-sm font-bold">{t("auth.otpCode")}</label>
                  <input data-testid="login-otp-input" required inputMode="numeric" maxLength={6}
                         pattern="[0-9]{6}"
                         className="sp-input mt-1.5 tracking-[0.4em] text-center text-xl font-mono"
                         placeholder="000000" value={otpCode}
                         onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} />
                </div>
                <button data-testid="login-otp-verify-btn" disabled={loading} className="sp-btn-primary w-full mt-2">
                  {loading ? t("auth.verifying") : t("auth.verifyCode")}
                </button>
                <button type="button" onClick={() => { setOtpStep(false); setOtpCode(""); }}
                        className="w-full text-xs text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]">
                  {t("auth.backToLogin")}
                </button>
              </>
            )}
          </form>

          <p className="text-sm text-[color:var(--sp-text-secondary)] mt-8">
            {t("auth.newSalon")}{" "}
            <Link data-testid="login-register-link" to="/register" className="text-[color:var(--sp-brand)] font-bold hover:underline">{t("auth.createAccount")}</Link>
          </p>
          <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-6 flex gap-3">
            <Link to="/legal/privacy" className="hover:text-[color:var(--sp-brand)]">{t("auth.privacyPolicy")}</Link>
            <span>·</span>
            <Link to="/legal/terms" className="hover:text-[color:var(--sp-brand)]">{t("auth.terms")}</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={SALON_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,14,26,0.25) 0%, rgba(11,14,26,0.7) 100%)" }} />
        <div className="relative h-full flex flex-col justify-between p-14">
          <SalonUpLogo className="h-10" white />
          <div>
            <p className="font-display text-4xl font-black leading-[1.05] text-white tracking-tight max-w-md">
              Un salone <span className="text-gradient">che funziona</span> anche quando il titolare non c&apos;è.
            </p>
            <p className="mt-4 text-sm text-white/70 max-w-md leading-relaxed">
              Memento è l&apos;asset tecnologico del metodo Salon Up.
            </p>
          </div>
          <div className="text-xs text-white/60 uppercase tracking-[0.28em]">Salone Mai Più Solo</div>
        </div>
      </div>
    </div>
  );
}
